import { FC } from 'react';

import Image from 'next/image';
import Link from 'next/link';
import { Trans, useTranslation } from 'next-i18next/pages';

import { Container } from '@//components/common';
import { useDocsList } from '@/api/docs/use-doc-list';
import {
	ARPP_URL,
	AVTOMATIZACIYA_KOMMUNIKACII_S_KLIENTAMI_URL,
	FISHPLACE_PHONE_URL,
	FISHPLACE_SUPPORT_EMAIL_URL,
	KATALOG_URL,
	MAKE_MAIN_URL,
	MAKE_SPRAVOCHNIY_CENTR_LINK,
	O_KOMPANII_URL,
	SK_URL,
	STORAGE_SEARCH_URL,
	TG_CHANNEL_FISHPLACE_URL,
	WHATSAPP_FISHPLACE_URL,
} from '@/constants/routes';
import { TranslationNamespace } from '@/i18n';
import { getMappingDocs } from '@/utils/docs';

import arppImage from './images/arpp.webp';
import topSpvImage from './images/top-spv.webp';
import { SkIcon, TelegramIcon, WhatsAppIcon } from './icons';

import styles from './Footer.module.scss';

export const Footer: FC = () => {
	const { t } = useTranslation(TranslationNamespace.Common);
	const { data: docsArray } = useDocsList();
	const registrationNumber = docsArray?.find((item) => item.title === '2024683715');
	const rusRegistry = docsArray?.find((item) => item.title === '28219');
	const { privacyPolicy, userAgreement, termOfUse } = getMappingDocs(docsArray ?? []);

	return (
		<footer className={styles.footer}>
			<Container className={styles.container}>
				<div>
					<div className={styles.footer__about}>
						<Link href={MAKE_MAIN_URL()} className={styles.footer__about__logo}>
							<img src="/img/svg/footer-logo.svg" alt="Fishplace" />
						</Link>
						<div className={styles.footer__about__content}>
							<span>&copy;&nbsp;{t('footer.company', { year: new Date().getFullYear() })}</span>
							<span>ИНН: 2536338363, ОГРН: 1232500008179</span>
						</div>
					</div>
					{registrationNumber?.file && rusRegistry?.file && (
						<span className={styles.footer__docs__info}>
							<Trans
								ns={TranslationNamespace.Common}
								i18nKey="footer.registration"
								components={{
									registration: <Link href={registrationNumber.file} target="_blank" />,
									registry: <Link href={rusRegistry.file} target="_blank" />,
								}}
							/>
						</span>
					)}
				</div>

				<div className={styles.footer__navigation}>
					<nav>
						<ul>
							<li>
								<Link href={STORAGE_SEARCH_URL}>{t('footer.for-fish-company')}</Link>
							</li>
							<li>
								<Link href={AVTOMATIZACIYA_KOMMUNIKACII_S_KLIENTAMI_URL}>
									{t('footer.for-warehouse')}
								</Link>
							</li>
							<li>
								<Link href={KATALOG_URL}>{t('footer.buy-fish')}</Link>
							</li>
							<li>
								<Link href={O_KOMPANII_URL}>{t('footer.about')}</Link>
							</li>
							<li>
								<Link href="/tariff" target="_blank" rel="noreferrer">
									{t('footer.tariffs')}
								</Link>
							</li>
							{privacyPolicy && (
								<li className={styles.sm}>
									<Link href={privacyPolicy} target="_blank" rel="noreferrer">
										{t('footer.privacy')}
									</Link>
								</li>
							)}
							{userAgreement && (
								<li className={styles.sm}>
									<Link href={userAgreement} target="_blank" rel="noreferrer">
										{t('footer.user-agreement')}
									</Link>
								</li>
							)}
							{termOfUse && (
								<li className={styles.sm}>
									<Link href={termOfUse} target="_blank" rel="noreferrer">
										{t('footer.license-agreement')}
									</Link>
								</li>
							)}
							<li>
								<Link href={MAKE_SPRAVOCHNIY_CENTR_LINK()} rel="noreferrer">
									{t('footer.help-center')}
								</Link>
							</li>
						</ul>
					</nav>
				</div>
				<div className={styles.footer__contacts}>
					<div className={styles.footer__contacts__item}>
						<div className={styles.footer__contacts__phone}>
							<a href={FISHPLACE_PHONE_URL}>+7 908 999 80 80</a>
						</div>
						<div className={styles.footer__contacts__block}>
							<div>
								<div className={styles.footer__contacts__socials}>
									<a href={TG_CHANNEL_FISHPLACE_URL} target="_blank" rel="noreferrer">
										<TelegramIcon />
									</a>
									<a href={WHATSAPP_FISHPLACE_URL} target="_blank" rel="noreferrer">
										<WhatsAppIcon />
									</a>
								</div>
							</div>
							<div className={styles.footer__contacts__email}>
								<a href={FISHPLACE_SUPPORT_EMAIL_URL}>hello@fishplace.ru</a>
							</div>
						</div>
					</div>
					<div className={styles.footer__badges}>
						<Link href={ARPP_URL} target="_blank">
							<Image src={arppImage} height={47} width={47} alt="АРПП" />
						</Link>
						<Image src={topSpvImage} height={46} width={32} alt={t('footer.made-in-top')} />
						<Link href={SK_URL} target="_blank">
							<SkIcon />
						</Link>
					</div>
				</div>
			</Container>
		</footer>
	);
};
